// Case Buddy domain module generated from CaseBuddyCore.js.
// Spreadsheet-shaped rows remain the source of truth.
import { CASE_ENTITY_KIND_EXCHANGE, CASE_ENTITY_KIND_NON_EXCHANGE, CASE_ENTITY_KIND_UNKNOWN } from './CaseBuddyAppConstants.js';
import { attributionLooksStructured, extractStructuredAttributionPart } from './CaseBuddyAttribution.js';
import { buildEffectiveEntityCurators, findCaseEntityCuratorMatch, formatCaseEntityKindLabel } from './CaseBuddyEntityCurators.js';
import { buildKnownEntityCasingPatterns, knownEntityCasingPatternMatchesAt, tokenizeKnownEntityCasingNotes } from './CaseBuddyKnownEntityCasing.js';
import { caseBuddyHashString, normalizeCell, rowHasMeaningfulData } from './CaseBuddyShared.js';
import { isMainTransactionPathLabel, isStandardNumericMainPathLabel, parseCaseEntries, rowLooksLikeCaseBuddyHeader } from './CaseBuddySheetData.js';
import {
  CASE_TRACE_DIRECTION_FORWARD,
  CASE_TRACE_DIRECTION_REVERSE,
  buildCaseTraceDirectionContextByRowIndex,
  normalizeCaseTraceDirection
} from './CaseBuddyTraceDirection.js';
import { normalizeCaseMatrix } from './CaseBuddyWorkbookState.js';

const CASE_ADDRESS_LEGEND_PATTERN_CACHE = new WeakMap();

export function getCaseAddressLegendEntityPatterns(effective = {}, expectedKind = '') {
  if (!effective || typeof effective !== 'object') {
    return buildKnownEntityCasingPatterns(effective)
      .filter(pattern => !expectedKind || pattern.kind === expectedKind)
      .sort((left, right) => right.words.length - left.words.length || right.name.length - left.name.length);
  }

  let patterns = CASE_ADDRESS_LEGEND_PATTERN_CACHE.get(effective);
  if (!patterns) {
    patterns = buildKnownEntityCasingPatterns(effective)
      .sort((left, right) => right.words.length - left.words.length || right.name.length - left.name.length);
    CASE_ADDRESS_LEGEND_PATTERN_CACHE.set(effective, patterns);
  }

  return expectedKind ? patterns.filter(pattern => pattern.kind === expectedKind) : patterns;
}

export function normalizeCaseAddressLegendAddressKey(value = '') {
  const text = normalizeCell(value)
    .replace(/\u00a0/g, ' ')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/^[`'"]+|[`'"]+$/g, '')
    .trim();
  if (!text) return '';
  if (/^0x[a-fA-F0-9]{40}$/.test(text)) return text.toLowerCase();
  if (/^(?:bc1|tb1|ltc1)/i.test(text)) return text.toLowerCase();
  if (/^(?:41[a-fA-F0-9]{40}|T[1-9A-HJ-NP-Za-km-z]{33})$/.test(text)) return text;
  return text;
}

export function caseAddressLegendContainsAny(text = '', patterns = []) {
  const lower = normalizeCell(text).toLowerCase();
  return patterns.some(pattern => lower.includes(pattern));
}

export function caseAddressLegendContainsHotWallet(text = '') {
  return caseAddressLegendContainsAny(text, ['hot wallet', 'hot-wallet', 'hotwallet']);
}

export function caseAddressLegendContainsDepositAddress(text = '') {
  return caseAddressLegendContainsAny(text, ['deposit address', 'deposit wallet', 'deposit account']);
}

export function caseAddressLegendContainsDormant(text = '') {
  return caseAddressLegendContainsAny(text, ['dormant address', 'dormant wallet', 'funds remain', 'funds remains', 'a portion of the funds remains']);
}

export function caseAddressLegendContainsUnknown(text = '') {
  return caseAddressLegendContainsAny(text, ['unknown address', 'unknown wallet', 'unknown recipient', 'unknown destination', 'unclear address']);
}

export function cleanCaseAddressLegendReverseAttributionName(value = '') {
  let text = normalizeCell(value)
    .replace(/\s+/g, ' ')
    .replace(/^(?:from|to)\s*:\s*/i, '')
    .replace(/\s*\((?:bridge|exchange|service|wallet|deposit)[^)]*\)\s*$/i, '')
    .replace(/^[`'"]+|[`'"]+$/g, '')
    .trim();
  if (!text || /^(?:unknown|unattributed|unidentified|none|n\/?a|null|not known|not identified)$/i.test(text)) return '';
  if (text.length > 96 || /[\r\n;]/.test(text)) return '';
  return text;
}

export function buildCaseAddressLegendReverseAttributionBaseLabel(value = '') {
  const attribution = cleanCaseAddressLegendReverseAttributionName(value);
  if (!attribution) return '';
  if (/\baddress$/i.test(attribution)) return attribution;
  if (/\b(?:deposit|hot wallet|wallet|account)$/i.test(attribution)) return `${attribution} Address`;
  return `${attribution} Address`;
}


export function caseAddressLegendContainsSwapBridgeAction(text = '') {
  return /\b(?:swapped|bridged)\b/i.test(normalizeCell(text));
}

export function analyzeCaseAddressLegendSwapBridgeSelfTransferRows(entryRows = []) {
  const rows = (Array.isArray(entryRows) ? entryRows : [])
    .map(item => Array.isArray(item) ? item : Array.isArray(item?.row) ? item.row : [])
    .filter(row => Array.isArray(row) && rowHasMeaningfulData(row));
  const fromKeys = new Set();
  const toKeys = new Set();
  const noteParts = [];

  rows.forEach((row) => {
    const fromKey = normalizeCaseAddressLegendAddressKey(row?.[2]);
    const toKey = normalizeCaseAddressLegendAddressKey(row?.[3]);
    if (fromKey) fromKeys.add(fromKey);
    if (toKey) toKeys.add(toKey);
    noteParts.push(row?.[9], row?.[11]);
  });

  const [fromKey = ''] = Array.from(fromKeys);
  const [toKey = ''] = Array.from(toKeys);
  const hasSingleSelfTransfer = fromKeys.size === 1 && toKeys.size === 1 && fromKey && toKey && fromKey === toKey;
  const hasSwapBridgeLanguage = caseAddressLegendContainsSwapBridgeAction(noteParts.filter(Boolean).join(' '));

  return {
    isSwapBridgeSelfTransfer: hasSingleSelfTransfer && hasSwapBridgeLanguage,
    hasSingleSelfTransfer,
    hasSwapBridgeLanguage,
    fromKey,
    toKey
  };
}

export function buildCaseAddressLegendSwapBridgeSelfTransferContextByRowIndex(rows = []) {
  const contextByRowIndex = new Map();
  parseCaseEntries(rows).forEach((entry) => {
    const entryRows = (entry.rows || []).map(item => item?.row || []);
    const context = analyzeCaseAddressLegendSwapBridgeSelfTransferRows(entryRows);
    (entry.rows || []).forEach((item) => {
      if (Number.isInteger(item?.rowIndex)) contextByRowIndex.set(item.rowIndex, context);
    });
  });
  return contextByRowIndex;
}

export function buildCaseAddressLegendTopLevelSourceContextByRowIndex(rows = [], effective = {}) {
  const contextByRowIndex = new Map();
  parseCaseEntries(rows).forEach((entry) => {
    const first = Array.isArray(entry?.rows) ? entry.rows[0] : null;
    const firstRow = Array.isArray(first?.row) ? first.row : [];
    const firstPathLabel = normalizeCell(firstRow?.[0]);
    const firstFromHash = normalizeCell(firstRow?.[14]);
    const isTopLevelEntry = !!normalizeCell(firstRow?.[1]) && !firstFromHash && (isMainTransactionPathLabel(firstPathLabel) || isStandardNumericMainPathLabel(firstPathLabel));
    if (!isTopLevelEntry) return;

    const notes = normalizeCell(firstRow?.[9]);
    const directional = extractCaseAddressLegendDirectionalContext(notes, effective);
    if (directional.source.owner === 'suspect') return;

    const sourceScope = directional.sourceClause
      ? directional.sourceClause
      : !directional.destination.owner && !directional.destination.entity
        ? notes
        : '';
    const victimEntity = directional.source.owner === 'victim'
      ? directional.source.entity
      : findCaseAddressLegendEntityInText(sourceScope, effective, CASE_ENTITY_KIND_EXCHANGE);

    const context = {
      isTopLevelSourceEntry: true,
      victimEntity,
      explicit: directional.source.owner === 'victim'
    };

    (entry.rows || []).forEach((item) => {
      if (Number.isInteger(item?.rowIndex)) contextByRowIndex.set(item.rowIndex, context);
    });
  });
  return contextByRowIndex;
}

export function caseAddressLegendContainsVictimReceiver(text = '') {
  return caseAddressLegendContainsAny(text, [
    "into the victim's address", "into victim's address", "to the victim's address", 'to victim address',
    'received by the victim', 'received by victim'
  ]);
}

export function caseAddressLegendContainsVictimSender(text = '') {
  return caseAddressLegendContainsAny(text, [
    "from the victim's address", 'from victim address', 'sent from the victim', 'funds move from the victim',
    'funds leave the victim', 'leaving the victim'
  ]);
}

export function caseAddressLegendContainsSuspectReceiver(text = '') {
  return caseAddressLegendContainsAny(text, [
    "into the suspect's address", "to the suspect's address", 'to suspect address', 'received by the suspect',
    'received by suspect', 'into the scammer', 'to the scammer'
  ]);
}

export function caseAddressLegendContainsSuspectSender(text = '') {
  return caseAddressLegendContainsAny(text, [
    "from the suspect's address", 'from suspect address', 'sent from the suspect', 'funds move from the suspect',
    'funds leave the suspect', 'leaving the suspect', 'from the scammer', 'sent from the scammer'
  ]);
}

export function findCaseAddressLegendEntityInText(text = '', effective = {}, expectedKind = '') {
  const source = normalizeCell(text);
  if (!source) return null;
  const patterns = getCaseAddressLegendEntityPatterns(effective, expectedKind);
  const tokens = tokenizeKnownEntityCasingNotes(source);

  for (let tokenIndex = 0; tokenIndex < tokens.length; tokenIndex += 1) {
    for (const pattern of patterns) {
      if (!knownEntityCasingPatternMatchesAt(tokens, tokenIndex, pattern)) continue;
      return {
        name: pattern.name,
        displayName: pattern.name,
        kind: pattern.kind,
        kindLabel: formatCaseEntityKindLabel(pattern.kind),
        source: pattern.source || '',
        sourceLabel: pattern.sourceLabel || '',
        matchedText: tokens.slice(tokenIndex, tokenIndex + pattern.words.length).map(token => token.rawText).join(' ')
      };
    }
  }

  return null;
}

export function readCaseAddressLegendScopedAttribution(row = [], side = 'to', options = {}) {
  const traceDirection = normalizeCaseTraceDirection(options.traceDirection) || CASE_TRACE_DIRECTION_FORWARD;

  if (traceDirection === CASE_TRACE_DIRECTION_REVERSE && side === 'from') {
    const fromAttribution = normalizeCell(row?.[23]).replace(/\s+/g, ' ');
    if (fromAttribution) {
      if (attributionLooksStructured(fromAttribution)) {
        return extractStructuredAttributionPart(fromAttribution, 'from')
          || extractStructuredAttributionPart(fromAttribution, 'to')
          || fromAttribution;
      }
      return fromAttribution;
    }

    // Compatibility with older reverse rows that stored a structured from-side
    // value in Column K before Column X was available.
    const legacyAttribution = normalizeCell(row?.[10]).replace(/\s+/g, ' ');
    return attributionLooksStructured(legacyAttribution)
      ? extractStructuredAttributionPart(legacyAttribution, 'from')
      : '';
  }

  const attribution = normalizeCell(row?.[10]).replace(/\s+/g, ' ');
  if (!attribution) return '';
  if (attributionLooksStructured(attribution)) return extractStructuredAttributionPart(attribution, side);

  // Forward behavior remains Tool17D-compatible: unstructured Column K labels
  // belong to the destination side unless the value is explicitly structured.
  return side === 'to' ? attribution : '';
}

export function extractCaseAddressLegendDirectionalClauses(notes = '') {
  if (!notes) {
    return {
      sourceClause: '',
      destinationClause: ''
    };
  }

  const text = String(notes);
  const sourceMatch = text.match(/\bfrom\b\s+(.+?)(?=\b(?:into|to|onto)\b|$)/i);
  const destinationMatch = text.match(/\b(?:into|to|onto)\b\s+(.+?)(?=\b(?:via|using|through)\b|$)/i);

  return {
    sourceClause: sourceMatch ? sourceMatch[1].trim() : '',
    destinationClause: destinationMatch ? destinationMatch[1].trim() : ''
  };
}

export function parseCaseAddressLegendOwnershipClause(clause = '', effective = {}) {
  if (!clause) {
    return {
      owner: null,
      entity: null,
      mentionsAddress: false,
      isDepositAddress: false,
      isHotWallet: false
    };
  }

  const text = String(clause);
  let owner = null;

  if (/\bvictim(?:'s)?\b/i.test(text)) {
    owner = 'victim';
  } else if (/\bsuspect(?:'s)?\b/i.test(text) || /\bscammer(?:'s)?\b/i.test(text)) {
    owner = 'suspect';
  }

  return {
    owner,
    entity: findCaseAddressLegendEntityInText(text, effective),
    mentionsAddress: /\baddress\b/i.test(text),
    isDepositAddress: /\bdeposit\s+address\b/i.test(text),
    isHotWallet: /\bhot\s+wallet\b/i.test(text)
  };
}

export function extractCaseAddressLegendDirectionalContext(notes = '', effective = {}) {
  const { sourceClause, destinationClause } = extractCaseAddressLegendDirectionalClauses(notes);

  return {
    sourceClause,
    destinationClause,
    source: parseCaseAddressLegendOwnershipClause(sourceClause, effective),
    destination: parseCaseAddressLegendOwnershipClause(destinationClause, effective)
  };
}

export function buildCaseAddressLegendOwnerBaseLabel(owner = '', entity = null) {
  const entityName = normalizeCell(entity?.displayName || entity?.name);
  if (owner === 'victim') return entityName ? `Victim's ${entityName} Address` : "Victim's Address";
  if (owner === 'suspect') return entityName ? `Suspect's ${entityName} Address` : "Suspect's Address";
  return '';
}

export function caseAddressLegendSelectNoteEntityScope(row = [], side = 'to', effective = {}) {
  const systemNotes = normalizeCell(row?.[9]);
  if (!systemNotes) return '';
  const context = extractCaseAddressLegendDirectionalContext(systemNotes, effective);

  if (side === 'from') {
    // Match Tool17D: a generic exchange mention in row notes should not be
    // copied onto the From Address. From-side note matching is limited to an
    // explicit source clause such as "from victim's Coinbase address".
    return context.sourceClause || '';
  }

  return context.destinationClause
    ? context.destinationClause
    : !context.source.owner && !context.source.entity
      ? systemNotes
      : '';
}

export function caseAddressLegendResolveEntity(row = [], side = 'to', effective = {}, options = {}) {
  const suppressAttributionEntityMatches = !!options.suppressAttributionEntityMatches;
  const suppressNoteEntityMatches = !!options.suppressNoteEntityMatches;
  const traceDirection = normalizeCaseTraceDirection(options.traceDirection) || CASE_TRACE_DIRECTION_FORWARD;
  const attribution = suppressAttributionEntityMatches ? '' : readCaseAddressLegendScopedAttribution(row, side, { traceDirection });
  const direct = attribution ? findCaseEntityCuratorMatch(effective, attribution) : null;
  if (direct?.isMatch) {
    return {
      name: direct.matchedName,
      displayName: direct.matchedName,
      kind: direct.entityKind || CASE_ENTITY_KIND_UNKNOWN,
      kindLabel: formatCaseEntityKindLabel(direct.entityKind || CASE_ENTITY_KIND_UNKNOWN),
      source: 'attribution',
      sourceLabel: direct.sourceLabel || 'Attribution',
      curatorSource: direct.source || '',
      matchedText: attribution,
      reason: side === 'from'
        ? traceDirection === CASE_TRACE_DIRECTION_REVERSE
          ? 'Column X From Address Attribution matched the effective curator.'
          : 'Structured Column K from attribution matched the effective curator.'
        : 'Column K attribution matched the effective curator.'
    };
  }

  const noteScope = suppressNoteEntityMatches
    ? ''
    : Object.prototype.hasOwnProperty.call(options, 'noteScope')
      ? normalizeCell(options.noteScope)
      : caseAddressLegendSelectNoteEntityScope(row, side, effective);
  const noteMatch = noteScope ? findCaseAddressLegendEntityInText(noteScope, effective) : null;
  if (noteMatch) {
    return {
      ...noteMatch,
      source: 'main_note',
      reason: side === 'from'
        ? 'Column J source clause matched the effective curator.'
        : 'Column J destination notes matched the effective curator.'
    };
  }

  return null;
}

export function createCaseAddressLegendObservation(row = [], rowIndex = 0, side = 'to', effective = {}, options = {}) {
  void rowIndex;
  const traceDirection = normalizeCaseTraceDirection(options.traceDirection) || CASE_TRACE_DIRECTION_FORWARD;
  const isReverse = traceDirection === CASE_TRACE_DIRECTION_REVERSE;
  const systemNotes = normalizeCell(row?.[9]);
  const toAttribution = normalizeCell(row?.[10]);
  const fromAttribution = normalizeCell(row?.[23]);
  const scopedAttribution = readCaseAddressLegendScopedAttribution(row, side, { traceDirection });
  const attribution = isReverse ? scopedAttribution : toAttribution;
  const caseNotes = isReverse ? normalizeCell(row?.[22]) : '';
  const combinedText = [systemNotes, attribution, caseNotes].filter(Boolean).join(' ');
  const sideLabel = side === 'from' ? 'From Address' : 'To Address';
  const chainId = normalizeCell(row?.[19]);
  const chainName = normalizeCell(row?.[20]);
  const pathLabel = normalizeCell(row?.[0]);
  const fromHash = normalizeCell(row?.[14]);
  const isTopLevelLead = !!normalizeCell(row?.[1]) && !fromHash && (isMainTransactionPathLabel(pathLabel) || isStandardNumericMainPathLabel(pathLabel));
  const context = extractCaseAddressLegendDirectionalContext(systemNotes, effective);
  const sideContext = side === 'from' ? context.source : context.destination;
  const swapBridgeSelfTransferContext = options.swapBridgeSelfTransferContext && typeof options.swapBridgeSelfTransferContext === 'object'
    ? options.swapBridgeSelfTransferContext
    : analyzeCaseAddressLegendSwapBridgeSelfTransferRows([row]);
  const isSwapBridgeSelfTransfer = !!swapBridgeSelfTransferContext.isSwapBridgeSelfTransfer;
  const swapBridgeSelfTransferEvidence = 'Swap/bridge self-transfer detected: exactly one From Address and one To Address match, and the entry notes say swapped or bridged. Service/entity names in those notes were ignored for From/To address labeling.';
  const finish = observation => isReverse
    ? {
      ...observation,
      traceDirection,
      fromAttribution,
      toAttribution,
      directionRelevantAttribution: attribution
    }
    : observation;

  if (!isReverse && sideContext.owner) {
    const baseLabel = buildCaseAddressLegendOwnerBaseLabel(sideContext.owner, sideContext.entity);
    return finish({
      baseLabel,
      labelFamily: sideContext.owner,
      sourceSide: side,
      sourceReason: sideContext.owner === 'victim' ? 'victim_note' : 'suspect_note',
      entityPresentedName: normalizeCell(sideContext.entity?.displayName),
      entityKind: sideContext.entity?.kind || '',
      score: sideContext.owner === 'victim' ? 720 : 710,
      chainId,
      chainName,
      evidence: [
        `${sideLabel} directional notes explicitly identify the ${sideContext.owner}${sideContext.entity?.displayName ? ` at ${sideContext.entity.displayName}` : ''}.`
      ]
    });
  }

  const topLevelSourceContext = options.topLevelSourceContext && typeof options.topLevelSourceContext === 'object'
    ? options.topLevelSourceContext
    : null;
  const isTopLevelSourceEntry = isTopLevelLead || !!topLevelSourceContext?.isTopLevelSourceEntry;

  // Preserve the established forward-only role rule exactly. A reverse trace is
  // source-of-funds analysis, so the starting sender is not automatically a victim.
  if (!isReverse && side === 'from' && isTopLevelSourceEntry && context.source.owner !== 'suspect') {
    const sourceScope = context.sourceClause
      ? context.sourceClause
      : !context.destination.owner && !context.destination.entity
        ? systemNotes
        : '';
    const victimEntity = isSwapBridgeSelfTransfer
      ? (context.source.owner === 'victim' ? context.source.entity : null)
      : context.source.owner === 'victim'
        ? context.source.entity
        : findCaseAddressLegendEntityInText(sourceScope, effective, CASE_ENTITY_KIND_EXCHANGE) || topLevelSourceContext?.victimEntity || null;
    return finish({
      baseLabel: buildCaseAddressLegendOwnerBaseLabel('victim', victimEntity),
      labelFamily: 'victim',
      sourceSide: side,
      sourceReason: 'top_level_source',
      entityPresentedName: normalizeCell(victimEntity?.displayName),
      entityKind: victimEntity?.kind || '',
      score: 650,
      chainId,
      chainName,
      evidence: [
        victimEntity?.displayName
          ? `Top-level starting/main entry sender treated as victim/source-side input for ${victimEntity.displayName}.`
          : 'Top-level starting/main entry sender treated as victim/source-side input.'
      ]
    });
  }

  const scopedEntity = caseAddressLegendResolveEntity(row, side, effective, {
    suppressAttributionEntityMatches: isSwapBridgeSelfTransfer,
    suppressNoteEntityMatches: isSwapBridgeSelfTransfer,
    traceDirection
  });
  const noteExchangeEntity = isSwapBridgeSelfTransfer
    ? null
    : findCaseAddressLegendEntityInText(systemNotes, effective, CASE_ENTITY_KIND_EXCHANGE);
  const hotWalletEntity = (side === 'to' || isReverse) && caseAddressLegendContainsHotWallet(combinedText)
    ? (scopedEntity?.kind === CASE_ENTITY_KIND_EXCHANGE ? scopedEntity : noteExchangeEntity)
    : null;

  if (hotWalletEntity?.kind === CASE_ENTITY_KIND_EXCHANGE) {
    return finish({
      baseLabel: `${hotWalletEntity.displayName} Hot Wallet Address`,
      labelFamily: 'exchange_hot',
      sourceSide: side,
      sourceReason: hotWalletEntity.source === 'attribution' ? 'attribution' : 'main_note',
      entityPresentedName: hotWalletEntity.displayName,
      entityKind: hotWalletEntity.kind,
      score: 430,
      chainId,
      chainName,
      evidence: [
        `${sideLabel} matched known exchange/service entity ${hotWalletEntity.displayName}.`,
        isReverse
          ? 'Hot-wallet language is present on the direction-relevant side of a reverse trace.'
          : 'Hot-wallet language is present on the receiving side, matching Tool17D hot-wallet labeling.'
      ]
    });
  }

  if (scopedEntity?.kind === CASE_ENTITY_KIND_EXCHANGE) {
    if (isReverse) {
      const isDepositAddress = caseAddressLegendContainsDepositAddress(combinedText)
        || /\bdeposit\b/i.test(attribution);
      return finish({
        baseLabel: isDepositAddress
          ? `${scopedEntity.displayName} Deposit Address`
          : `${scopedEntity.displayName} Address`,
        labelFamily: isDepositAddress ? 'exchange_deposit' : 'exchange_generic',
        sourceSide: side,
        sourceReason: scopedEntity.source === 'attribution' ? 'attribution' : side === 'from' ? 'main_from_note' : 'main_note',
        entityPresentedName: scopedEntity.displayName,
        entityKind: scopedEntity.kind,
        score: isDepositAddress ? 440 : 400,
        chainId,
        chainName,
        evidence: [
          `${sideLabel} matched known exchange/service entity ${scopedEntity.displayName}.`,
          isDepositAddress
            ? 'Deposit-address language is present; the reverse-trace label stays descriptive and role-neutral.'
            : 'Reverse-trace entity labels remain descriptive without assigning victim or suspect ownership.'
        ]
      });
    }

    if (side === 'to') {
      const useDepositAddressLabels = !!options.useDepositAddressLabels;
      return finish({
        baseLabel: useDepositAddressLabels
          ? `${scopedEntity.displayName} Deposit Address`
          : `Suspect's ${scopedEntity.displayName} Address`,
        labelFamily: useDepositAddressLabels ? 'exchange_deposit' : 'exchange_suspect',
        sourceSide: side,
        sourceReason: scopedEntity.source === 'attribution' ? 'attribution' : 'main_note',
        entityPresentedName: scopedEntity.displayName,
        entityKind: scopedEntity.kind,
        score: caseAddressLegendContainsDepositAddress(combinedText) ? 420 : 400,
        chainId,
        chainName,
        evidence: [
          `${sideLabel} matched known exchange/service entity ${scopedEntity.displayName}.`,
          useDepositAddressLabels
            ? caseAddressLegendContainsDepositAddress(combinedText)
              ? 'Deposit-address language is present on the receiving side.'
              : 'Exchange/service receiver defaulted to optional deposit-address labeling.'
            : 'Exchange/service receiver used Tool17D-style suspect entity labeling.'
        ]
      });
    }

    return finish({
      baseLabel: `Suspect's ${scopedEntity.displayName} Address`,
      labelFamily: 'exchange_suspect',
      sourceSide: side,
      sourceReason: scopedEntity.source === 'attribution' ? 'attribution' : 'main_from_note',
      entityPresentedName: scopedEntity.displayName,
      entityKind: scopedEntity.kind,
      score: 260,
      chainId,
      chainName,
      evidence: [
        `${sideLabel} had a side-scoped exchange/service match for ${scopedEntity.displayName}.`,
        'From-side exchange/service matches are not treated as hot wallets unless the receiving-side row says hot wallet.'
      ]
    });
  }

  if (scopedEntity?.kind === CASE_ENTITY_KIND_NON_EXCHANGE) {
    if (isReverse) {
      return finish({
        baseLabel: `${scopedEntity.displayName} Address`,
        labelFamily: 'non_exchange',
        sourceSide: side,
        sourceReason: scopedEntity.source === 'attribution' ? 'attribution' : side === 'from' ? 'main_from_note' : 'main_note',
        entityPresentedName: scopedEntity.displayName,
        entityKind: scopedEntity.kind,
        score: 310,
        chainId,
        chainName,
        evidence: [
          `${sideLabel} matched known non-exchange entity ${scopedEntity.displayName}.`,
          'Reverse-trace entity labels remain descriptive without assigning victim or suspect ownership.'
        ]
      });
    }

    return finish({
      baseLabel: `Suspect's ${scopedEntity.displayName} Address`,
      labelFamily: 'non_exchange',
      sourceSide: side,
      sourceReason: scopedEntity.source === 'attribution' ? 'attribution' : side === 'from' ? 'main_from_note' : 'main_note',
      entityPresentedName: scopedEntity.displayName,
      entityKind: scopedEntity.kind,
      score: 300,
      chainId,
      chainName,
      evidence: [`${sideLabel} matched known non-exchange entity ${scopedEntity.displayName}; Tool17D-style suspect entity labeling was used.`]
    });
  }

  if (isReverse) {
    const attributedLabel = buildCaseAddressLegendReverseAttributionBaseLabel(attribution);
    if (attributedLabel) {
      return finish({
        baseLabel: attributedLabel,
        labelFamily: 'attributed',
        sourceSide: side,
        sourceReason: 'attribution',
        entityPresentedName: cleanCaseAddressLegendReverseAttributionName(attribution),
        entityKind: CASE_ENTITY_KIND_UNKNOWN,
        score: 280,
        chainId,
        chainName,
        evidence: [
          `${sideLabel} used the direction-relevant ${side === 'from' ? 'Column X From Address Attribution' : 'Column K To Address Attribution'} value “${normalizeCell(attribution)}”.`,
          'Reverse-trace attribution labels remain descriptive without assigning victim or suspect ownership.'
        ]
      });
    }
  }

  if (!isReverse && side === 'to' && caseAddressLegendContainsVictimReceiver(combinedText)) {
    return finish({ baseLabel: "Victim's Address", labelFamily: 'victim', sourceSide: side, sourceReason: 'victim_note', score: 190, chainId, chainName, evidence: [`${sideLabel} notes indicate victim receiver language.`] });
  }

  if (!isReverse && side === 'from' && caseAddressLegendContainsVictimSender(combinedText)) {
    return finish({ baseLabel: "Victim's Address", labelFamily: 'victim', sourceSide: side, sourceReason: 'victim_note', score: 190, chainId, chainName, evidence: [`${sideLabel} notes indicate victim sender language.`] });
  }

  if (!isReverse && ((side === 'to' && caseAddressLegendContainsSuspectReceiver(combinedText)) || (side === 'from' && caseAddressLegendContainsSuspectSender(combinedText)))) {
    return finish({ baseLabel: "Suspect's Address", labelFamily: 'suspect', sourceSide: side, sourceReason: 'suspect_note', score: 180, chainId, chainName, evidence: [`${sideLabel} notes indicate suspect/scammer language.`] });
  }

  if (caseAddressLegendContainsDormant(combinedText)) {
    if (isReverse || options.labelDormantAddressesSeparately) {
      return finish({ baseLabel: 'Dormant Address', labelFamily: 'dormant', sourceSide: side, sourceReason: 'dormant_note', score: 120, chainId, chainName, evidence: [`${sideLabel} notes indicate dormant/funds-remain language.`] });
    }
    return finish({ baseLabel: "Suspect's Address", labelFamily: 'suspect', sourceSide: side, sourceReason: 'dormant_note', score: 115, chainId, chainName, evidence: [`${sideLabel} notes indicate dormant/funds-remain language; dormant-specific labeling is off.`] });
  }

  if (caseAddressLegendContainsUnknown(combinedText)) {
    return finish({ baseLabel: 'Unknown Address', labelFamily: 'unknown', sourceSide: side, sourceReason: 'unknown_note', score: 90, chainId, chainName, evidence: [`${sideLabel} notes describe the address as unknown/unclear.`] });
  }

  if (isReverse && side === 'from' && isTopLevelSourceEntry) {
    return finish({
      baseLabel: 'Starting Address',
      labelFamily: 'trace_start',
      sourceSide: side,
      sourceReason: 'reverse_trace_start',
      score: 140,
      chainId,
      chainName,
      evidence: ['Top-level reverse-trace source is the starting address for source-of-funds analysis; no victim or suspect ownership was inferred.']
    });
  }

  const defaultEvidencePrefix = isSwapBridgeSelfTransfer ? [swapBridgeSelfTransferEvidence] : [];

  if (isReverse) {
    return finish({
      baseLabel: 'Unknown Address',
      labelFamily: 'unknown',
      sourceSide: side,
      sourceReason: 'reverse_default',
      score: side === 'from' ? 70 : 65,
      chainId,
      chainName,
      evidence: [
        ...defaultEvidencePrefix,
        `No stronger ${side === 'from' ? 'source-side' : 'receiving-side'} classification was found in this reverse trace; no victim or suspect role was inferred.`
      ]
    });
  }

  return side === 'to'
    ? finish({ baseLabel: "Suspect's Address", labelFamily: 'suspect', sourceSide: side, sourceReason: 'default_to_side', score: 80, chainId, chainName, evidence: [...defaultEvidencePrefix, 'No stronger receiving-side classification was found; defaulted receiver to suspect-side address.'] })
    : finish({ baseLabel: 'Unknown Address', labelFamily: 'unknown', sourceSide: side, sourceReason: 'default_from_side', score: 60, chainId, chainName, evidence: [...defaultEvidencePrefix, 'No stronger sending-side classification was found; defaulted sender to unknown address.'] });
}

export function addCaseAddressLegendDraftObservation(draftsByKey, rawAddress = '', row = [], rowIndex = 0, side = 'to', observation = {}) {
  const displayAddress = normalizeCell(rawAddress);
  const key = normalizeCaseAddressLegendAddressKey(displayAddress);
  if (!key || !displayAddress || !observation?.baseLabel) return;
  let draft = draftsByKey.get(key);
  if (!draft) {
    draft = {
      normalizedAddress: key,
      preferredDisplayAddress: displayAddress,
      rawAddressVariants: [],
      firstSeenRowIndex: rowIndex,
      firstSeenRowNumber: rowIndex + 1,
      observations: [],
      best: null
    };
    draftsByKey.set(key, draft);
  }
  if (!draft.rawAddressVariants.includes(displayAddress)) draft.rawAddressVariants.push(displayAddress);
  if (rowIndex < draft.firstSeenRowIndex) {
    draft.firstSeenRowIndex = rowIndex;
    draft.firstSeenRowNumber = rowIndex + 1;
    draft.preferredDisplayAddress = displayAddress;
  }
  const normalizedObservation = {
    ...observation,
    rowIndex,
    rowNumber: rowIndex + 1,
    pathLabel: normalizeCell(row?.[0]),
    transactionHash: normalizeCell(row?.[1]),
    side,
    sideLabel: side === 'from' ? 'From Address' : 'To Address',
    address: displayAddress,
    notes: normalizeCell(row?.[9]),
    attribution: normalizeCell(observation?.directionRelevantAttribution || row?.[10]),
    ...(normalizeCaseTraceDirection(observation?.traceDirection) === CASE_TRACE_DIRECTION_REVERSE
      ? {
        toAttribution: normalizeCell(row?.[10]),
        fromAttribution: normalizeCell(row?.[23]),
        traceDirection: CASE_TRACE_DIRECTION_REVERSE
      }
      : {})
  };
  draft.observations.push(normalizedObservation);
  if (!draft.best || (Number(normalizedObservation.score) || 0) > (Number(draft.best.score) || 0)) {
    draft.best = normalizedObservation;
  }
}

export function compareCaseAddressLegendNatural(left = '', right = '') {
  return normalizeCell(left).localeCompare(normalizeCell(right), undefined, { numeric: true, sensitivity: 'base' });
}

export function buildCaseAddressLegendCandidatesFromDrafts(draftsByKey = new Map(), existingEntries = []) {
  const existingByAddress = new Map();
  (Array.isArray(existingEntries) ? existingEntries : []).forEach((entry) => {
    const key = normalizeCaseAddressLegendAddressKey(entry?.address || entry?.Address || entry?.preferredDisplayAddress);
    if (key && !existingByAddress.has(key)) existingByAddress.set(key, entry);
  });

  const groupsByBaseLabel = new Map();
  Array.from(draftsByKey.values()).forEach((draft) => {
    if (!draft?.best?.baseLabel) return;
    if (!groupsByBaseLabel.has(draft.best.baseLabel)) groupsByBaseLabel.set(draft.best.baseLabel, []);
    groupsByBaseLabel.get(draft.best.baseLabel).push(draft);
  });

  const candidates = [];
  groupsByBaseLabel.forEach((drafts) => {
    drafts.sort((left, right) => {
      if (left.firstSeenRowIndex !== right.firstSeenRowIndex) return left.firstSeenRowIndex - right.firstSeenRowIndex;
      return compareCaseAddressLegendNatural(left.preferredDisplayAddress, right.preferredDisplayAddress);
    });
    drafts.forEach((draft, index) => {
      const best = draft.best || {};
      const proposedLabel = `${best.baseLabel} #${index + 1}`;
      const existing = existingByAddress.get(draft.normalizedAddress) || null;
      const existingLabel = normalizeCell(existing?.label || existing?.Label || existing?.proposedLegendLabel || existing?.ProposedLegendLabel);
      const existingAddress = normalizeCell(existing?.address || existing?.Address || existing?.preferredDisplayAddress || existing?.PreferredDisplayAddress);
      const existingChainId = normalizeCell(existing?.chainId || existing?.ChainId);
      const existingChainName = normalizeCell(existing?.chainName || existing?.ChainName);
      const changed = !existing || existingLabel !== proposedLabel || existingAddress !== draft.preferredDisplayAddress || existingChainId !== normalizeCell(best.chainId) || existingChainName !== normalizeCell(best.chainName);
      const bestEvidence = Array.isArray(best.evidence) ? best.evidence : [];
      const supplementalEvidence = (draft.observations || [])
        .filter(item => item !== best)
        .flatMap(item => item.evidence || [])
        .filter(item => !/^No stronger .+ defaulted /i.test(normalizeCell(item)));
      const evidence = [...new Set([...bestEvidence, ...supplementalEvidence])];
      const candidate = {
        id: `address-legend-${caseBuddyHashString(`${draft.normalizedAddress}|${proposedLabel}`)}`,
        key: `address-legend-${caseBuddyHashString(`${draft.normalizedAddress}|${proposedLabel}`)}`,
        statusLabel: changed ? 'Needs rebuild' : 'Current',
        normalizedAddress: draft.normalizedAddress,
        preferredDisplayAddress: draft.preferredDisplayAddress,
        rawAddressVariants: draft.rawAddressVariants || [],
        existingLegendLabel: existingLabel,
        proposedLegendLabel: proposedLabel,
        labelFamily: best.labelFamily || 'unknown',
        labelFamilyLabel: formatCaseAddressLegendFamily(best.labelFamily || 'unknown'),
        sourceSide: best.sourceSide || best.side || '',
        sourceSideLabel: best.sourceSide === 'from' ? 'From' : best.sourceSide === 'to' ? 'To' : '',
        sourceReason: best.sourceReason || '',
        sourceReasonLabel: formatCaseAddressLegendSourceReason(best.sourceReason || ''),
        entityPresentedName: best.entityPresentedName || '',
        entityKind: best.entityKind || '',
        ...(normalizeCaseTraceDirection(best.traceDirection) === CASE_TRACE_DIRECTION_REVERSE
          ? { traceDirection: CASE_TRACE_DIRECTION_REVERSE }
          : {}),
        chainId: normalizeCell(best.chainId),
        chainName: normalizeCell(best.chainName),
        firstSeenRowIndex: draft.firstSeenRowIndex,
        firstSeenRowNumber: draft.firstSeenRowNumber,
        observationCount: (draft.observations || []).length,
        observations: draft.observations || [],
        evidence,
        isChanged: changed,
        isReviewable: true,
        notes: buildCaseAddressLegendEntryNotes({ ...best, evidence }),
        searchText: ''
      };
      candidate.searchText = [
        candidate.proposedLegendLabel,
        candidate.existingLegendLabel,
        candidate.preferredDisplayAddress,
        candidate.normalizedAddress,
        candidate.labelFamilyLabel,
        candidate.sourceReasonLabel,
        candidate.chainId,
        candidate.chainName,
        candidate.entityPresentedName,
        ...candidate.evidence,
        ...(candidate.observations || []).flatMap(item => [
          item.rowNumber,
          item.pathLabel,
          item.transactionHash,
          item.notes,
          item.attribution,
          ...(normalizeCaseTraceDirection(item.traceDirection) === CASE_TRACE_DIRECTION_REVERSE
            ? [item.fromAttribution, item.toAttribution, CASE_TRACE_DIRECTION_REVERSE]
            : [])
        ])
      ].map(value => normalizeCell(value).toLowerCase()).filter(Boolean).join(' ');
      candidates.push(candidate);
    });
  });

  return candidates.sort((left, right) => {
    if (left.isChanged !== right.isChanged) return left.isChanged ? -1 : 1;
    if (left.firstSeenRowIndex !== right.firstSeenRowIndex) return left.firstSeenRowIndex - right.firstSeenRowIndex;
    return compareCaseAddressLegendNatural(left.proposedLegendLabel, right.proposedLegendLabel);
  });
}

export function formatCaseAddressLegendFamily(family = '') {
  const labels = {
    exchange_deposit: 'Exchange deposit',
    exchange_hot: 'Exchange hot wallet',
    exchange_suspect: 'Exchange/service suspect label',
    exchange_generic: 'Exchange/service',
    non_exchange: 'Non-exchange',
    attributed: 'Attributed',
    dormant: 'Dormant',
    unknown: 'Unknown',
    trace_start: 'Starting address',
    victim: 'Victim',
    suspect: 'Suspect'
  };
  return labels[normalizeCell(family)] || normalizeCell(family) || 'Unknown';
}

export function formatCaseAddressLegendSourceReason(reason = '') {
  const labels = {
    attribution: 'Attribution',
    main_note: 'System notes',
    main_from_note: 'System notes',
    dormant_note: 'Dormant note',
    unknown_note: 'Unknown note',
    victim_note: 'Victim note',
    suspect_note: 'Suspect note',
    top_level_source: 'Top-level source',
    reverse_trace_start: 'Reverse trace start',
    reverse_default: 'Reverse trace default',
    default_to_side: 'Default receiver',
    default_from_side: 'Default sender',
    override: 'Override'
  };
  return labels[normalizeCell(reason)] || normalizeCell(reason) || 'Rule';
}

export function getCaseAddressLegendEntryLabel(entry = {}) {
  return normalizeCell(entry?.label || entry?.Label || entry?.proposedLegendLabel || entry?.ProposedLegendLabel || entry?.proposedLabel);
}

export function getCaseAddressLegendEntryAddress(entry = {}) {
  return normalizeCell(entry?.address || entry?.Address || entry?.preferredDisplayAddress || entry?.PreferredDisplayAddress || entry?.normalizedAddress || entry?.NormalizedAddress);
}

export function caseAddressLegendLabelHasSpecificEntity(label = '') {
  const text = normalizeCell(label);
  if (!text) return false;
  if (/^suspect(?:'s|s)?\s+address\s*#/i.test(text)) return false;
  if (/^victim(?:'s|s)?\s+address\s*#/i.test(text)) return false;
  return /(?:^suspect(?:'s|s)?\s+.+\s+address\s*#|^victim(?:'s|s)?\s+.+\s+address\s*#|hot\s+wallet\s+address\s*#|deposit\s+(?:address|wallet|account)\s*#)/i.test(text);
}

export function getCaseAddressLegendSortGroup(entry = {}) {
  const label = getCaseAddressLegendEntryLabel(entry).toLowerCase();
  const family = normalizeCell(entry?.labelFamily || entry?.LabelFamily || entry?.family || entry?.Family).toLowerCase();
  if (family === 'victim' || /^victim(?:'s)?\b/.test(label)) return { rank: 0, key: 'victim' };
  if (family === 'trace_start' || /^starting\s+address\b/.test(label)) return { rank: 0, key: 'trace-start' };
  if (caseAddressLegendLabelHasSpecificEntity(label) || /^exchange(?:_|$)/i.test(family) || family === 'non_exchange' || family === 'unknown') return { rank: 1, key: 'entity-other' };
  if (family === 'suspect' || /^suspect(?:'s)?\b/.test(label) || /^scammer(?:'s)?\b/.test(label)) return { rank: 2, key: 'suspect-generic' };
  return { rank: 1, key: 'entity-other' };
}

export function getCaseAddressLegendLabelNumber(label = '') {
  const matches = [...String(label || '').matchAll(/#\s*(\d+)/g)];
  if (!matches.length) return Number.POSITIVE_INFINITY;
  const parsed = Number(matches[matches.length - 1][1]);
  return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
}

export function getCaseAddressLegendLabelRoot(label = '') {
  let text = normalizeCell(label)
    .replace(/#\s*\d+\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  text = text.replace(/^(?:victim|suspect|scammer)(?:'s|s)?\s+/i, '').trim();
  text = text.replace(/\s+(?:deposit\s+)?(?:hot\s+wallet\s+)?address$/i, '').trim();
  text = text.replace(/\s+(?:deposit\s+wallet|deposit\s+account|hot\s+wallet)$/i, '').trim();
  return text.toLowerCase();
}

export function getCaseAddressLegendLabelSubtypeRank(label = '') {
  const text = normalizeCell(label);
  if (/^victim(?:'s|s)?\s+.+\s+address\s*#/i.test(text)) return 0;
  if (/^suspect(?:'s|s)?\s+.+\s+address\s*#/i.test(text)) return 1;
  if (/deposit\s+(?:address|wallet|account)\s*#/i.test(text)) return 2;
  if (/hot\s+wallet\s+address\s*#/i.test(text)) return 3;
  return 4;
}

export function compareCaseAddressLegendEntriesForDisplay(left = {}, right = {}) {
  const leftGroup = getCaseAddressLegendSortGroup(left);
  const rightGroup = getCaseAddressLegendSortGroup(right);
  if (leftGroup.rank !== rightGroup.rank) return leftGroup.rank - rightGroup.rank;

  const leftLabel = getCaseAddressLegendEntryLabel(left);
  const rightLabel = getCaseAddressLegendEntryLabel(right);
  const leftRoot = getCaseAddressLegendLabelRoot(leftLabel);
  const rightRoot = getCaseAddressLegendLabelRoot(rightLabel);
  if (leftRoot !== rightRoot) {
    return leftRoot.localeCompare(rightRoot, undefined, { numeric: true, sensitivity: 'base' });
  }

  const leftSubtype = getCaseAddressLegendLabelSubtypeRank(leftLabel);
  const rightSubtype = getCaseAddressLegendLabelSubtypeRank(rightLabel);
  if (leftSubtype !== rightSubtype) return leftSubtype - rightSubtype;

  const leftNumber = getCaseAddressLegendLabelNumber(leftLabel);
  const rightNumber = getCaseAddressLegendLabelNumber(rightLabel);
  if (leftNumber !== rightNumber) return leftNumber - rightNumber;

  const labelCompare = compareCaseAddressLegendNatural(leftLabel, rightLabel);
  if (labelCompare !== 0) return labelCompare;
  return compareCaseAddressLegendNatural(getCaseAddressLegendEntryAddress(left), getCaseAddressLegendEntryAddress(right));
}

export function sortCaseAddressLegendEntriesForDisplay(entries = []) {
  return (Array.isArray(entries) ? entries : [])
    .filter(entry => getCaseAddressLegendEntryAddress(entry) || getCaseAddressLegendEntryLabel(entry))
    .map((entry, index) => ({ entry, index }))
    .sort((left, right) => compareCaseAddressLegendEntriesForDisplay(left.entry, right.entry) || left.index - right.index)
    .map(item => item.entry);
}

export function getCaseAddressLegendEntriesForOutput(analysis = {}, options = {}) {
  const preferredEntries = Array.isArray(options.entries)
    ? options.entries
    : analysis?.buildIsCurrent && Array.isArray(analysis?.storedEntries) && analysis.storedEntries.length
      ? analysis.storedEntries
      : Array.isArray(analysis?.entries)
        ? analysis.entries
        : [];
  return sortCaseAddressLegendEntriesForDisplay(preferredEntries);
}

export function buildCaseAddressLegendQuickAccessRows(analysis = {}, options = {}) {
  const rows = [['Address', 'Label']];
  getCaseAddressLegendEntriesForOutput(analysis, options).forEach((entry) => {
    const address = getCaseAddressLegendEntryAddress(entry);
    const label = getCaseAddressLegendEntryLabel(entry);
    if (!address && !label) return;
    rows.push([address, label]);
  });
  return rows;
}

export function buildCaseAddressLegendEntryNotes(candidate = {}) {
  const parts = [];
  const family = formatCaseAddressLegendFamily(candidate.labelFamily);
  const source = formatCaseAddressLegendSourceReason(candidate.sourceReason);
  if (family) parts.push(`Family: ${family}.`);
  if (source) parts.push(`Source: ${source}.`);
  if (candidate.sourceSide) parts.push(`Side: ${candidate.sourceSide === 'from' ? 'From' : 'To'}.`);
  const traceDirection = normalizeCaseTraceDirection(candidate.traceDirection);
  if (traceDirection === CASE_TRACE_DIRECTION_REVERSE) parts.push('Trace: Reverse.');
  const evidence = Array.isArray(candidate.evidence) ? candidate.evidence : [];
  if (evidence.length) parts.push(`Evidence: ${evidence.slice(0, 3).join(' ')}`);
  return parts.join(' ').trim();
}

export function buildCaseAddressLegendEntriesFromCandidates(candidates = []) {
  return (Array.isArray(candidates) ? candidates : []).map((candidate) => ({
    label: normalizeCell(candidate.proposedLegendLabel),
    address: normalizeCell(candidate.preferredDisplayAddress),
    normalizedAddress: normalizeCell(candidate.normalizedAddress),
    chainId: normalizeCell(candidate.chainId),
    chainName: normalizeCell(candidate.chainName),
    notes: buildCaseAddressLegendEntryNotes(candidate),
    labelFamily: normalizeCell(candidate.labelFamily),
    sourceSide: normalizeCell(candidate.sourceSide),
    sourceReason: normalizeCell(candidate.sourceReason),
    entityPresentedName: normalizeCell(candidate.entityPresentedName),
    ...(normalizeCaseTraceDirection(candidate.traceDirection) === CASE_TRACE_DIRECTION_REVERSE
      ? { traceDirection: CASE_TRACE_DIRECTION_REVERSE }
      : {}),
    firstSeenRowNumber: candidate.firstSeenRowNumber || '',
    observationCount: candidate.observationCount || 0
  }));
}

export function buildCaseAddressLegendBasisHash(candidates = []) {
  const payload = (Array.isArray(candidates) ? candidates : []).map(candidate => ({
    address: candidate.normalizedAddress,
    display: candidate.preferredDisplayAddress,
    label: candidate.proposedLegendLabel,
    family: candidate.labelFamily,
    side: candidate.sourceSide,
    reason: candidate.sourceReason,
    direction: normalizeCaseTraceDirection(candidate.traceDirection) === CASE_TRACE_DIRECTION_REVERSE
      ? CASE_TRACE_DIRECTION_REVERSE
      : undefined,
    chainId: candidate.chainId,
    chainName: candidate.chainName,
    evidence: candidate.evidence || [],
    rows: (candidate.observations || []).map(item => [item.rowNumber, item.side, item.pathLabel, item.transactionHash])
  })).sort((left, right) => `${left.label}|${left.address}`.localeCompare(`${right.label}|${right.address}`));
  return `address-legend-${caseBuddyHashString(JSON.stringify(payload))}`;
}

export function countCaseAddressLegendStaleEntries(entries = [], candidates = []) {
  const current = new Set((Array.isArray(candidates) ? candidates : []).map(candidate => normalizeCaseAddressLegendAddressKey(candidate.preferredDisplayAddress)).filter(Boolean));
  return (Array.isArray(entries) ? entries : []).filter((entry) => {
    const key = normalizeCaseAddressLegendAddressKey(entry?.address || entry?.Address);
    return key && !current.has(key);
  }).length;
}

export function buildCaseAddressLegendAnalysis(mainData = [], effectiveCurators = {}, workflowState = {}, options = {}) {
  const rows = normalizeCaseMatrix(mainData);
  const effective = effectiveCurators?.all ? effectiveCurators : buildEffectiveEntityCurators({}, {});
  const review = workflowState?.addressLegendReview && typeof workflowState.addressLegendReview === 'object'
    ? workflowState.addressLegendReview
    : {};
  const labelDormantAddressesSeparately = options.labelDormantAddressesSeparately ?? !!review.labelDormantAddressesSeparately;
  const useDepositAddressLabels = options.useDepositAddressLabels ?? !!review.useDepositAddressLabels;
  const draftsByKey = new Map();
  const traceDirectionContextByRowIndex = buildCaseTraceDirectionContextByRowIndex(rows);
  const swapBridgeSelfTransferContextByRowIndex = buildCaseAddressLegendSwapBridgeSelfTransferContextByRowIndex(rows);
  const topLevelSourceContextByRowIndex = buildCaseAddressLegendTopLevelSourceContextByRowIndex(rows, effective);
  let rowCountWithAddress = 0;
  let occurrenceCount = 0;

  rows.forEach((row, rowIndex) => {
    if (rowIndex === 0 || rowLooksLikeCaseBuddyHeader(row) || !rowHasMeaningfulData(row)) return;
    const fromAddress = normalizeCell(row?.[2]);
    const toAddress = normalizeCell(row?.[3]);
    if (fromAddress || toAddress) rowCountWithAddress += 1;
    const traceDirectionContext = traceDirectionContextByRowIndex.get(rowIndex) || { traceDirection: CASE_TRACE_DIRECTION_FORWARD };
    const traceDirection = normalizeCaseTraceDirection(traceDirectionContext.traceDirection) || CASE_TRACE_DIRECTION_FORWARD;
    const swapBridgeSelfTransferContext = swapBridgeSelfTransferContextByRowIndex.get(rowIndex) || analyzeCaseAddressLegendSwapBridgeSelfTransferRows([row]);
    const topLevelSourceContext = topLevelSourceContextByRowIndex.get(rowIndex) || null;
    if (fromAddress) {
      occurrenceCount += 1;
      addCaseAddressLegendDraftObservation(draftsByKey, fromAddress, row, rowIndex, 'from', createCaseAddressLegendObservation(row, rowIndex, 'from', effective, { labelDormantAddressesSeparately, useDepositAddressLabels, swapBridgeSelfTransferContext, topLevelSourceContext, traceDirection }));
    }
    if (toAddress) {
      occurrenceCount += 1;
      addCaseAddressLegendDraftObservation(draftsByKey, toAddress, row, rowIndex, 'to', createCaseAddressLegendObservation(row, rowIndex, 'to', effective, { labelDormantAddressesSeparately, useDepositAddressLabels, swapBridgeSelfTransferContext, topLevelSourceContext, traceDirection }));
    }
  });

  const storedEntries = Array.isArray(review.entries) ? review.entries : [];
  const candidates = buildCaseAddressLegendCandidatesFromDrafts(draftsByKey, storedEntries);
  const currentBasisHash = buildCaseAddressLegendBasisHash(candidates);
  const storedBuildBasisHash = normalizeCell(review.builtBasisHash);
  const confirmedReviewBasisHash = normalizeCell(review.reviewedBasisHash || review.confirmedReviewBasisHash);
  const changedCount = candidates.filter(candidate => candidate.isChanged).length;
  const staleLegendEntryCount = countCaseAddressLegendStaleEntries(storedEntries, candidates);
  const buildIsCurrent = candidates.length > 0 && changedCount === 0 && staleLegendEntryCount === 0 && storedBuildBasisHash === currentBasisHash;
  const reviewIsCurrent = buildIsCurrent && confirmedReviewBasisHash === currentBasisHash;
  const entries = buildCaseAddressLegendEntriesFromCandidates(candidates);

  let summary = '';
  if (candidates.length === 0) {
    summary = 'No From/To addresses were found in Main Data for the address legend.';
  } else if (!buildIsCurrent) {
    summary = `${candidates.length} legend entr${candidates.length === 1 ? 'y is' : 'ies are'} proposed. ${changedCount} changed/new entr${changedCount === 1 ? 'y' : 'ies'} and ${staleLegendEntryCount} stale stored entr${staleLegendEntryCount === 1 ? 'y' : 'ies'} need rebuild.`;
  } else if (!reviewIsCurrent) {
    summary = `Address legend is built for the current case data. Review ${candidates.length} entr${candidates.length === 1 ? 'y' : 'ies'} and mark reviewed when it looks right.`;
  } else {
    summary = `Address legend is built and reviewed for the current case data (${candidates.length} entr${candidates.length === 1 ? 'y' : 'ies'}).`;
  }

  return {
    candidates,
    entries,
    storedEntries,
    candidateCount: candidates.length,
    legendEntryCount: storedEntries.length,
    changedCount,
    staleLegendEntryCount,
    rowCountWithAddress,
    occurrenceCount,
    currentBasisHash,
    storedBuildBasisHash,
    confirmedReviewBasisHash,
    buildIsCurrent,
    reviewIsCurrent,
    labelDormantAddressesSeparately,
    useDepositAddressLabels,
    builtAtUtc: normalizeCell(review.builtAtUtc),
    reviewedAtUtc: normalizeCell(review.reviewedAtUtc),
    senderCount: candidates.filter(candidate => candidate.sourceSide === 'from').length,
    receiverCount: candidates.filter(candidate => candidate.sourceSide === 'to').length,
    exchangeDerivedCount: candidates.filter(candidate => String(candidate.labelFamily || '').startsWith('exchange')).length,
    summary,
    effectiveCuratorCounts: {
      exchange: effective.remoteExchangeCount || (effective.exchange || []).length || 0,
      nonExchange: effective.remoteNonExchangeCount || (effective.nonExchange || []).length || 0,
      localExchangeAdditions: effective.overlay?.exchangeAdditions?.length || 0,
      localNonExchangeAdditions: effective.overlay?.nonExchangeAdditions?.length || 0
    }
  };
}

export function buildCaseAddressLegendDeliverableRows(analysis = {}, options = {}) {
  const entries = getCaseAddressLegendEntriesForOutput(analysis, options);
  const rows = [['Label', 'Address', 'Chain ID', 'Chain Name', 'Notes']];
  entries.forEach((entry) => {
    rows.push([
      getCaseAddressLegendEntryLabel(entry),
      getCaseAddressLegendEntryAddress(entry),
      normalizeCell(entry.chainId || entry.ChainId),
      normalizeCell(entry.chainName || entry.ChainName),
      normalizeCell(entry.notes || entry.Notes)
    ]);
  });
  return rows;
}

export function buildCaseAddressLegendAuditRows(analysis = {}, actionLabel = 'Address legend review') {
  const rows = [
    ['Case Buddy Address Legend Audit', '', '', '', '', '', '', '', '', 'Audit only. Address legend is separate case support/deliverable state; Main Data is not modified.'],
    ['Action', actionLabel, 'Candidate Count', analysis.candidateCount || 0, 'Changed', analysis.changedCount || 0, 'Stale Stored', analysis.staleLegendEntryCount || 0, 'Basis', analysis.currentBasisHash || ''],
    ['Status', analysis.summary || '', 'Built Current', analysis.buildIsCurrent ? 'Yes' : 'No', 'Review Current', analysis.reviewIsCurrent ? 'Yes' : 'No', 'Dormant Separate', analysis.labelDormantAddressesSeparately ? 'Yes' : 'No'],
    [],
    ['Proposed Address Legend'],
    ...buildCaseAddressLegendDeliverableRows(analysis),
    [],
    ['Review Candidates'],
    ['Label', 'Address', 'Existing Label', 'Family', 'Source', 'First Seen Row', 'Chain ID', 'Chain Name', 'Evidence']
  ];
  (analysis.candidates || []).forEach((candidate) => {
    rows.push([
      candidate.proposedLegendLabel || '',
      candidate.preferredDisplayAddress || '',
      candidate.existingLegendLabel || '',
      candidate.labelFamilyLabel || candidate.labelFamily || '',
      candidate.sourceReasonLabel || candidate.sourceReason || '',
      candidate.firstSeenRowNumber || '',
      candidate.chainId || '',
      candidate.chainName || '',
      (candidate.evidence || []).join(' ')
    ]);
  });
  return rows;
}
